import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiGlobe, FiRefreshCw, FiSave, FiUsers } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';
import './LocalizationManagement.css';

const NAMESPACES = ['common', 'settings', 'nav', 'admin'];

const LocalizationManagement = () => {
  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    default_locale: 'en',
    enabled_locales: ['en'],
    localization_enabled: true,
  });
  const [supported, setSupported] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_locale: [] });
  const [locale, setLocale] = useState('en');
  const [namespace, setNamespace] = useState('common');
  const [items, setItems] = useState([]);
  const [edited, setEdited] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes, transRes] = await Promise.all([
        axios.get(`${API_URL}/admin/localization/settings`, { headers: headers() }),
        axios.get(`${API_URL}/admin/localization/user-stats`, { headers: headers() }),
        axios.get(`${API_URL}/admin/localization/translations`, {
          headers: headers(),
          params: { locale, namespace },
        }),
      ]);
      setSettings({
        default_locale: settingsRes.data?.default_locale || 'en',
        enabled_locales: settingsRes.data?.enabled_locales || ['en'],
        localization_enabled: settingsRes.data?.localization_enabled !== false,
      });
      setSupported(settingsRes.data?.supported_locales || []);
      setStats(statsRes.data || { total: 0, by_locale: [] });
      setItems(transRes.data?.items || []);
      setEdited({});
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load localization data');
    } finally {
      setLoading(false);
    }
  }, [locale, namespace]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleEnabled = (code) => {
    setSettings((prev) => {
      const set = new Set(prev.enabled_locales || []);
      if (set.has(code)) {
        if (code === prev.default_locale) return prev;
        set.delete(code);
      } else set.add(code);
      return { ...prev, enabled_locales: Array.from(set) };
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/localization/settings`, settings, { headers: headers() });
      toast.success('Language settings saved');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveTranslations = async () => {
    const changes = Object.entries(edited).map(([key, value]) => ({ key, value }));
    if (!changes.length) {
      toast.info('No translation changes to save');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/localization/translations`, {
        locale,
        namespace,
        items: changes,
      }, { headers: headers() });
      toast.success(`Saved ${changes.length} translation(s)`);
      setEdited({});
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save translations');
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => items.map((item) => ({
    ...item,
    value: edited[item.key] !== undefined ? edited[item.key] : item.value,
  })), [items, edited]);

  if (loading && !items.length) {
    return <div className="admin-page"><p>Loading localization…</p></div>;
  }

  return (
    <div className="admin-page loc-page">
      <div className="page-header">
        <div>
          <h1><FiGlobe /> Localization</h1>
          <p>Manage supported languages, translations, and track user language preferences.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={fetchAll}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div className="loc-grid">
        <section className="loc-card loc-card--feature">
          <h2>Localization feature</h2>
          <label className="loc-feature-toggle">
            <input
              type="checkbox"
              checked={settings.localization_enabled !== false}
              onChange={(e) => setSettings((p) => ({
                ...p,
                localization_enabled: e.target.checked,
              }))}
            />
            <span>Enable localization on frontend</span>
          </label>
          <p className="loc-muted">
            When disabled, language switchers are hidden and the app uses the default language only.
            Changes sync to Web and PIF within ~45 seconds.
          </p>
          <button type="button" className="btn-primary" onClick={saveSettings} disabled={saving}>
            <FiSave /> Save feature settings
          </button>
        </section>

        <section className="loc-card">
          <h2><FiUsers /> User language preferences</h2>
          <p className="loc-muted">Total users: <strong>{stats.total}</strong></p>
          <div className="loc-stats">
            {(stats.by_locale || []).map((s) => (
              <div key={s.locale} className="loc-stat-row">
                <span>{s.label || s.locale}</span>
                <span className="loc-stat-count">{s.count}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="loc-card">
          <h2>Enabled languages</h2>
          <div className="loc-lang-grid">
            {supported.map((l) => {
              const enabled = (settings.enabled_locales || []).includes(l.code);
              return (
                <label key={l.code} className={`loc-lang-chip ${enabled ? 'on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={() => toggleEnabled(l.code)}
                  />
                  <span>{l.nativeName}</span>
                  <small>{l.name}</small>
                </label>
              );
            })}
          </div>
          <div className="loc-default-row">
            <label>Default language</label>
            <select
              value={settings.default_locale}
              onChange={(e) => setSettings((p) => ({ ...p, default_locale: e.target.value }))}
            >
              {(settings.enabled_locales || ['en']).map((code) => {
                const meta = supported.find((s) => s.code === code);
                return <option key={code} value={code}>{meta?.nativeName || code}</option>;
              })}
            </select>
          </div>
          <button type="button" className="btn-primary" onClick={saveSettings} disabled={saving}>
            <FiSave /> Save language settings
          </button>
        </section>
      </div>

      <section className="loc-card loc-translations">
        <div className="loc-trans-head">
          <h2>Translation content</h2>
          <div className="loc-filters">
            <select value={locale} onChange={(e) => setLocale(e.target.value)}>
              {(settings.enabled_locales || ['en']).map((code) => {
                const meta = supported.find((s) => s.code === code);
                return <option key={code} value={code}>{meta?.nativeName || code}</option>;
              })}
            </select>
            <select value={namespace} onChange={(e) => setNamespace(e.target.value)}>
              {NAMESPACES.map((ns) => <option key={ns} value={ns}>{ns}</option>)}
            </select>
            <button type="button" className="btn-primary" onClick={saveTranslations} disabled={saving}>
              <FiSave /> Save translations
            </button>
          </div>
        </div>
        <div className="loc-table-wrap">
          <table className="loc-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td><code>{row.key}</code></td>
                  <td>
                    <input
                      className="loc-input"
                      value={row.value || ''}
                      onChange={(e) => setEdited((p) => ({ ...p, [row.key]: e.target.value }))}
                    />
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr><td colSpan={2} className="loc-empty">No translations for this locale/namespace.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default LocalizationManagement;
